import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import ReactSlider from 'react-slider';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import {
  AddPlus,
  MinusCircle,
} from '@picsio/icons';
import { IconButton } from '@picsio/ui';
import s from './RangeSlider.module.scss';
import Tooltip from '../../Tooltip';
import Logger from '../../../services/Logger';
import sendEventToIntercom from '../../../services/IntercomEventService';

const sliderLabels = [
  { name: 'Broad', value: 0 },
  { name: 'Medium', value: 25 },
  { name: 'Balanced', value: 50 },
  { name: 'Precise', value: 75 },
  { name: 'Exact', value: 100 },
];

// Convert from range 10-50 to range 0-100 (for user display)
// KNN range: 0.1-0.5, real value: 10-50, display: 0-100%
const convertToDisplayValue = (realValue) => ((realValue - 10) / 40) * 100;

// Convert from range 0-100 (from user) to range 10-50 (real value)
const convertToRealValue = (userDisplayValue) => Number((10 + (userDisplayValue / 100) * 40).toFixed(0));

const RangeSliderByText = ({
  initValue = 30, // Real value in range 10-50 (default 30 = 50% in display)
  onChange,
  onSliderClick,
  onAfterChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const min = 0; // Display range for user
  const max = 100; // Display range for user
  const sliderRef = useRef(null);

  const displayValue = useMemo(() => convertToDisplayValue(initValue), [initValue]);
  const [value, setValue] = useState(displayValue);

  const closestLabel = useMemo(() => sliderLabels.reduce((prev, curr) => (Math.abs(curr.value - value) < Math.abs(prev.value - value) ? curr : prev)), [value]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (sliderRef.current && !sliderRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLabelClick = (e) => {
    const labelDisplayValue = Number(e.currentTarget.dataset.value);
    const realValue = convertToRealValue(labelDisplayValue);
    const closest = sliderLabels.reduce((prev, curr) => (Math.abs(curr.value - labelDisplayValue) < Math.abs(prev.value - labelDisplayValue) ? curr : prev));
    Logger.log('User', 'KNNScorePanelPresetClicked', {
      similarity_level: labelDisplayValue,
      similarity_preset: closest.name,
    });
    setValue(labelDisplayValue);

    if (onAfterChange) onAfterChange(realValue);
    if (onChange) onChange(realValue);
  };

  const handleChange = (userDisplayValue) => {
    setValue(userDisplayValue);
    // Convert display value (0-100) to real value (10-50)
    const realValue = convertToRealValue(userDisplayValue);
    if (onChange) onChange(realValue);
  };

  const handleAfterChange = (userDisplayValue) => {
    // Convert display value (0-100) to real value (10-50)
    const realValue = convertToRealValue(userDisplayValue);
    const closest = sliderLabels.reduce((prev, curr) => (Math.abs(curr.value - userDisplayValue) < Math.abs(prev.value - userDisplayValue) ? curr : prev));

    Logger.log('User', 'KNNScorePanelPresetClicked', {
      similarity_level: userDisplayValue,
      similarity_preset: closest.name,
    });

    if (onAfterChange) onAfterChange(realValue);
  };

  const handleChangeInput = (e) => {
    let newValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    newValue = newValue.replace(/^0+(?=\d)/, ''); // Remove leading zeros
    newValue = Number(newValue);
    Logger.log('User', 'KNNScorePanelNumericLevelChanged', {
      similarity_level: newValue,
      action: 'input',
    });
    setValue(newValue);
  };

  const handleBlurInput = (e) => {
    let inputDisplayValue = e.target.value;
    if (inputDisplayValue === '') inputDisplayValue = min;
    inputDisplayValue = Number(inputDisplayValue);
    if (inputDisplayValue < min) inputDisplayValue = min;
    if (inputDisplayValue > max) inputDisplayValue = max;
    setValue(inputDisplayValue);
    // Convert display value (0-100) to real value (10-50)
    const realValue = convertToRealValue(inputDisplayValue);
    if (onChange) onChange(realValue);
    if (onAfterChange) onAfterChange(realValue);
  };

  const handleKeyDownInput = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleValueIncrement = () => {
    let newDisplayValue = value + 1;
    if (newDisplayValue > max) newDisplayValue = max;
    const realValue = convertToRealValue(newDisplayValue);
    Logger.log('User', 'SimilarityScorePanelBtnLevelChanged', {
      similarity_level: newDisplayValue,
      action: 'increment',
    });
    setValue(newDisplayValue);
    if (onChange) onChange(realValue);
    if (onAfterChange) onAfterChange(realValue);
  };

  const handleValueDecrement = () => {
    let newDisplayValue = value - 1;
    if (newDisplayValue < min) newDisplayValue = min;
    const realValue = convertToRealValue(newDisplayValue);
    Logger.log('User', 'KNNScorePanelBtnLevelChanged', {
      similarity_level: newDisplayValue,
      action: 'decrement',
    });
    setValue(newDisplayValue);
    if (onChange) onChange(realValue);
    if (onAfterChange) onAfterChange(realValue);
  };

  const toggleSliderOpen = () => {
    if (!isOpen) {
      Logger.log('User', 'KNNScoreDropdownClicked', {
        initial_preset: closestLabel.name,
      });
      sendEventToIntercom('PrecisionScoreDropdownOpened');
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={s.rangeSlider} ref={sliderRef}>
      <Tooltip content={<div style={{ textAlign: 'center' }}>Drag the Visual Search <br /> precision slider to control how closely <br /> results should match your query.<br />Lower values explore broader matches;<br />higher values return more precise results.</div>} placement="right">
        <div onClick={toggleSliderOpen} className={cn(s.trigger, { [s.isOpen]: isOpen })} role="presentation" data-testid="similarityScoreTrigger">
          <span className={s.triggerText}>
            {closestLabel.name} ({Math.trunc(value)}%)
          </span>
          <i className={s.arrow} />
        </div>
      </Tooltip>

      <CSSTransition
        unmountOnExit
        in={isOpen}
        timeout={300}
        classNames="fade"
      >
        <div className={s.container}>
          <div className={s.header}>
            <div className={s.title}>Precision level</div>
            <div className={s.valueContainer}>
              <IconButton
                onClick={handleValueDecrement}
                size="md"
                disabled={value <= min}
                componentProps={{
                  'data-testid': 'similarityScoreDecrementBtn',
                }}
              >
                <MinusCircle />
              </IconButton>
              <input
                id="range-slider-input"
                name="range-slider-input"
                type="text"
                value={Math.trunc(value)}
                onBlur={handleBlurInput}
                onKeyDown={handleKeyDownInput}
                onChange={handleChangeInput}
                className={s.value}
                data-testid="similarityScoreInput"
              />
              <IconButton
                onClick={handleValueIncrement}
                size="md"
                disabled={value >= max}
                componentProps={{
                  'data-testid': 'similarityScoreIncrementBtn',
                }}
              >
                <AddPlus />
              </IconButton>
            </div>
          </div>
          <div className={s.sliderWrapper}>
            <ReactSlider
              min={min}
              max={max}
              value={Math.trunc(value)}
              step={1}
              className={s.slider}
              handleClassName={s.dot}
              barClassName={s.line}
              onChange={handleChange}
              onAfterChange={handleAfterChange}
              onSliderClick={onSliderClick}
            />
            <div>
              {sliderLabels.map((label) => {
                const isPassed = Number(value) >= label.value;
                const isActive = Number(value) === label.value;

                return (
                  <div
                    key={label.name}
                    className={cn(s.sliderLabel, {
                      [s.passed]: isPassed,
                      [s.active]: isActive,
                    })}
                    data-value={label.value}
                    onClick={handleLabelClick}
                    role="presentation"
                    data-testid={`similarityScoreLabel-${label.name}`}
                  >
                    <div className={s.sliderLabelText}>{label.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CSSTransition>

    </div>
  );
};

export default RangeSliderByText;
